import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Clock, Zap, Truck, Activity } from 'lucide-react';
import { fetchLiveStatus } from '../lib/engine';

export default function LiveStatusPanel() {
  const [data, setData] = useState({
    active_vehicles: 3,
    last_intervention_location: 'Gebze',
    last_intervention_road: 'D-100 Karayolu',
    last_intervention_minutes_ago: 8,
    avg_arrival_time: 28,
    last_hour_interventions: 4,
  });
  const [pulse, setPulse] = useState(false);
  const prevData = useRef(data);

  useEffect(() => {
    let active = true;

    const load = async () => {
      const result = await fetchLiveStatus();
      if (result && active) {
        // Detect if location changed for pulse animation
        if (result.last_intervention_location !== prevData.current.last_intervention_location) {
          setPulse(true);
          setTimeout(() => setPulse(false), 800);
        }
        prevData.current = result;
        setData(result);
      }
    };

    load();
    const interval = setInterval(load, 12000);
    return () => { active = false; clearInterval(interval); };
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const metrics = [
    {
      icon: Truck,
      value: `${data.active_vehicles}`,
      label: 'Araç Sahada',
      sub: 'Şu an aktif',
      color: 'var(--green)',
      bg: 'var(--green-dim)',
      border: 'var(--green-border)',
    },
    {
      icon: MapPin,
      value: data.last_intervention_location,
      label: data.last_intervention_road,
      sub: `${data.last_intervention_minutes_ago} dk önce`,
      color: 'var(--blue)',
      bg: 'var(--blue-dim)',
      border: 'var(--blue-border)',
      dynamic: true,
    },
    {
      icon: Clock,
      value: `${data.avg_arrival_time} dk`,
      label: 'Ort. Varış',
      sub: 'Kocaeli geneli',
      color: '#ff9500',
      bg: 'rgba(255,149,0,.1)',
      border: 'rgba(255,149,0,.25)',
    },
    {
      icon: Activity,
      value: `${data.last_hour_interventions}`,
      label: 'Son 1 Saat',
      sub: 'Müdahale yapıldı',
      color: 'var(--red)',
      bg: 'var(--red-dim)',
      border: 'var(--red-border)',
    },
  ];

  return (
    <section
      data-testid="live-status-panel"
      style={{
        background: 'var(--bg2)',
        borderTop: '1px solid var(--border)',
        borderBottom: '1px solid var(--border)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div style={{
        position: 'absolute', top: '50%', left: '50%',
        transform: 'translate(-50%,-50%)',
        width: 600, height: 400,
        background: 'radial-gradient(ellipse, rgba(16,185,129,.04) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      <div className="wrap" style={{ position: 'relative', zIndex: 1 }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '1.5rem 0 .5rem', flexWrap: 'wrap', gap: '.75rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem' }}>
            <div style={{
              width: 12, height: 12, borderRadius: '50%',
              background: 'var(--green)',
              boxShadow: '0 0 16px rgba(16,185,129,.6)',
            }} className="live" />
            <span style={{
              fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700,
              fontSize: '.95rem', color: 'var(--green)',
              letterSpacing: '.06em', textTransform: 'uppercase',
            }}>
              Canlı Sistem Durumu
            </span>
          </div>
          <span style={{
            fontSize: '.78rem', color: 'var(--text2)',
            fontFamily: "'Space Grotesk',sans-serif",
            letterSpacing: '.04em',
          }}>
            Otomatik güncelleniyor
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0 }} className="live-grid">
          {metrics.map(({ icon: Icon, value, label, sub, color, bg, border, dynamic }, i) => (
            <div
              key={label}
              data-testid={`live-metric-${i}`}
              style={{
                padding: '2.25rem 1.5rem',
                borderRight: i < 3 ? '1px solid var(--border)' : 'none',
                display: 'flex', alignItems: 'center', gap: '1.125rem',
                transition: 'background .5s ease',
                background: (dynamic && pulse) ? 'rgba(74,143,245,.04)' : 'transparent',
              }}
            >
              <div style={{
                width: 60, height: 60, borderRadius: 16,
                background: bg, border: `1px solid ${border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: color, flexShrink: 0,
                transition: 'transform .4s ease',
                transform: (dynamic && pulse) ? 'scale(1.08)' : 'scale(1)',
                boxShadow: `0 4px 20px ${color}15`,
              }}>
                <Icon size={26} strokeWidth={1.75} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{
                  fontFamily: "'Space Grotesk',sans-serif", fontWeight: 800,
                  fontSize: '1.5rem', color: 'var(--text)', lineHeight: 1.1,
                  letterSpacing: '-.02em',
                  transition: 'opacity .3s ease',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>{value}</div>
                <div style={{
                  fontSize: '.85rem', color: 'var(--text2)', marginTop: '.2rem',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>{label}</div>
                <div style={{ fontSize: '.75rem', color: color, marginTop: '.15rem', fontWeight: 600 }}>{sub}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @media(max-width:768px){
          .live-grid{grid-template-columns:repeat(2,1fr)!important}
          .live-grid>div:nth-child(2){border-right:none!important}
          .live-grid>div:nth-child(1),.live-grid>div:nth-child(2){border-bottom:1px solid var(--border)}
          .live-grid>div:nth-child(3){border-right:1px solid var(--border)!important}
        }
        @media(max-width:480px){
          .live-grid{grid-template-columns:1fr!important}
          .live-grid>div{border-right:none!important;border-bottom:1px solid var(--border)}
          .live-grid>div:last-child{border-bottom:none}
        }
      `}</style>
    </section>
  );
}
